// Serviço para integração com Assijus (Governo Brasileiro)
// Documentação: https://github.com/assijus/assijus

interface AssijusDocument {
  id: number;
  code: string;
  description?: string;
}

interface AssijusHash {
  sha1: string;
  sha256: string;
}

interface AssijusSignature {
  envelope: string;
  certificate?: string;
}

interface AssijusConfig {
  ui: 'bootstrap-3';
  iframePopupUrl?: string;
  docs: AssijusDocument[];
  beginCallback?: () => void;
  hashCallback: (id: number, callback: (hash: AssijusHash) => void) => void;
  saveCallback: (id: number, signature: AssijusSignature, callback: (result: { success: boolean }) => void) => void;
  errorCallback?: (id: number, error: any, callback: () => void) => void;
  endCallback?: () => void;
}

declare global {
  interface Window {
    produzirAssinaturaDigital: (config: AssijusConfig) => void;
  }
}

export class AssijusService {
  private static instance: AssijusService;
  private isLoaded = false;

  private constructor() {}

  public static getInstance(): AssijusService {
    if (!AssijusService.instance) {
      AssijusService.instance = new AssijusService();
    }
    return AssijusService.instance;
  }

  /**
   * Carrega o script do Assijus
   */
  public async loadAssijus(): Promise<boolean> {
    if (this.isLoaded) {
      return true;
    }

    return new Promise((resolve, reject) => {
      // Verificar se já existe
      if (window.produzirAssinaturaDigital) {
        this.isLoaded = true;
        resolve(true);
        return;
      }

      // Carregar script do Assijus
      const script = document.createElement('script');
      script.src = 'https://assijus.trf2.jus.br/assijus/popup-api.js';
      script.onload = () => {
        this.isLoaded = true;
        resolve(true);
      };
      script.onerror = () => {
        reject(new Error('Falha ao carregar Assijus'));
      };

      document.head.appendChild(script);
    });
  }

  /**
   * Assina um documento PDF usando Assijus
   */
  public async signDocument(
    documentId: string,
    documentCode: string,
    pdfFile: File,
    onSuccess: (signedPdf: Uint8Array) => void,
    onError: (error: string) => void
  ): Promise<void> {
    try {
      // 1. Carregar Assijus
      await this.loadAssijus();

      // 2. Calcular hash SHA-256 do PDF
      const pdfBuffer = await pdfFile.arrayBuffer();
      const hashBuffer = await crypto.subtle.digest('SHA-256', pdfBuffer);
      const hashArray = new Uint8Array(hashBuffer);
      const hashBase64 = btoa(String.fromCharCode(...hashArray));

      console.log('🔐 Assinando com Assijus:', documentCode);

      // 3. Configurar Assijus
      const config: AssijusConfig = {
        ui: 'bootstrap-3',
        iframePopupUrl: 'https://assijus.trf2.jus.br/assijus/popup.html',
        docs: [
          {
            id: parseInt(documentId),
            code: documentCode,
            description: `Documento ${documentCode}`
          }
        ],
        beginCallback: () => {
          console.log('🔐 Assijus: Iniciando assinatura...');
        },
        hashCallback: (id, callback) => {
          console.log('🔐 Assijus: Retornando hash do documento...');
          // Retornar hash SHA-256 do PDF
          callback({
            sha1: '', // Não usado para PDF
            sha256: hashBase64
          });
        },
        saveCallback: (id, signature, callback) => {
          console.log('✅ Assijus: Assinatura recebida!');
          
          try {
            // Converter envelope para PDF assinado
            const signedPdf = this.convertSignatureToPdf(pdfBuffer, signature.envelope);
            onSuccess(signedPdf);
            callback({ success: true });
          } catch (error) {
            console.error('❌ Erro ao processar assinatura:', error);
            onError('Erro ao processar assinatura do Assijus');
            callback({ success: false });
          }
        },
        errorCallback: (id, error, callback) => {
          console.error('❌ Assijus: Erro na assinatura:', error);
          onError(`Erro do Assijus: ${error.message || 'Erro desconhecido'}`);
          callback();
        },
        endCallback: () => {
          console.log('🔐 Assijus: Processo finalizado');
        }
      };

      // 4. Executar assinatura
      window.produzirAssinaturaDigital(config);

    } catch (error) {
      console.error('❌ Erro ao configurar Assijus:', error);
      onError('Falha ao carregar Assijus');
    }
  }

  /**
   * Converte a assinatura do Assijus em PDF assinado
   */
  private convertSignatureToPdf(originalPdfBuffer: ArrayBuffer, signatureEnvelope: string): Uint8Array {
    // Por enquanto, retorna o PDF original
    // Em uma implementação completa, seria necessário:
    // 1. Decodificar o envelope CMS/PKCS#7
    // 2. Aplicar a assinatura ao PDF
    // 3. Retornar o PDF assinado
    
    console.log('🔐 Convertendo assinatura para PDF...');
    console.log('📦 Envelope recebido:', signatureEnvelope.length, 'chars');
    
    return new Uint8Array(originalPdfBuffer);
  }

  /**
   * Verifica se Assijus está disponível
   */
  public isAvailable(): boolean {
    return this.isLoaded && !!window.produzirAssinaturaDigital;
  }
}

export default AssijusService;
